/**
 * Bank Linking Utilities
 * Utilities for loading Plaid and Ninja SDKs dynamically
 */

// ==================== Plaid SDK Loading ====================

const PLAID_CDN_URL = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js'
let plaidScriptLoaded = false
let plaidScriptLoading = false

/**
 * Load Plaid JavaScript SDK dynamically
 * @param elementRef - DOM element reference to append script to
 * @returns Promise that resolves when script is loaded
 */
export function loadPlaidSDK(elementRef: HTMLElement | null): Promise<void> {
  return new Promise((resolve, reject) => {
    if (plaidScriptLoaded) {
      resolve()
      return
    }

    if (plaidScriptLoading) {
      // Wait for existing load to complete
      const checkInterval = setInterval(() => {
        if (plaidScriptLoaded) {
          clearInterval(checkInterval)
          resolve()
        }
      }, 100)
      return
    }

    // Check if script already exists
    const existingScript = document.querySelector('script.plaidjs')
    if (existingScript) {
      plaidScriptLoaded = true
      resolve()
      return
    }

    if (!elementRef) {
      reject(new Error('Element reference is required to load Plaid SDK'))
      return
    }

    plaidScriptLoading = true

    const script = document.createElement('script')
    script.src = PLAID_CDN_URL
    script.type = 'text/javascript'
    script.className = 'plaidjs'
    script.async = true

    script.onload = () => {
      plaidScriptLoaded = true
      plaidScriptLoading = false
      resolve()
    }

    script.onerror = () => {
      plaidScriptLoading = false
      reject(new Error('Failed to load Plaid SDK'))
    }

    elementRef.appendChild(script)
  })
}

/**
 * Check if Plaid SDK is loaded
 */
export function isPlaidSDKLoaded(): boolean {
  return typeof window !== 'undefined' && 'Plaid' in window && plaidScriptLoaded
}

// ==================== Ninja SDK Loading ====================

let ninjaScriptLoaded = false
let ninjaScriptLoading = false
let ninjaScriptUrl: string | null = null

/**
 * Load Ninja JavaScript SDK dynamically
 * @param jsUrl - Ninja JS URL from configuration
 * @param elementRef - DOM element reference to append script to
 * @returns Promise that resolves when script is loaded
 */
export function loadNinjaSDK(
  jsUrl: string,
  elementRef: HTMLElement | null
): Promise<void> {
  return new Promise((resolve, reject) => {
    // If same URL already loaded, resolve immediately
    if (ninjaScriptLoaded && ninjaScriptUrl === jsUrl) {
      resolve()
      return
    }

    if (ninjaScriptLoading) {
      // Wait for existing load to complete
      const checkInterval = setInterval(() => {
        if (ninjaScriptLoaded && ninjaScriptUrl === jsUrl) {
          clearInterval(checkInterval)
          resolve()
        }
      }, 100)
      return
    }

    // Check if script already exists
    const existingScript = document.querySelector('script.ninjaJs')
    if (existingScript && ninjaScriptUrl === jsUrl) {
      ninjaScriptLoaded = true
      resolve()
      return
    }

    if (!elementRef) {
      reject(new Error('Element reference is required to load Ninja SDK'))
      return
    }

    if (!jsUrl) {
      reject(new Error('Ninja JS URL is required'))
      return
    }

    // Remove old script if URL changed
    if (existingScript && ninjaScriptUrl !== jsUrl) {
      existingScript.remove()
      ninjaScriptLoaded = false
    }

    ninjaScriptLoading = true
    ninjaScriptUrl = jsUrl

    const script = document.createElement('script')
    script.src = jsUrl
    script.type = 'text/javascript'
    script.className = 'ninjaJs'
    script.async = true

    script.onload = () => {
      ninjaScriptLoaded = true
      ninjaScriptLoading = false
      resolve()
    }

    script.onerror = () => {
      ninjaScriptLoading = false
      ninjaScriptUrl = null
      reject(new Error('Failed to load Ninja SDK'))
    }

    elementRef.appendChild(script)
  })
}

/**
 * Check if Ninja SDK is loaded
 */
export function isNinjaSDKLoaded(): boolean {
  return (
    typeof window !== 'undefined' &&
    'NinjaFetchWidget' in window &&
    ninjaScriptLoaded
  )
}

// ==================== Type Declarations ====================

declare global {
  interface Window {
    Plaid?: {
      create: (config: PlaidConfig) => PlaidHandler
    }
    NinjaFetchWidget?: {
      mount: (config: NinjaConfig) => void
      unmount?: () => void
    }
    plaidresponse?: (
      type: 'success' | 'error' | 'event',
      data: unknown,
      token: string
    ) => void
    ninjaresponse?: (
      type: 'success' | 'error' | 'close',
      data: unknown,
      token: string
    ) => void
  }
}

export interface PlaidConfig {
  token: string
  onSuccess: (publicToken: string, metadata: PlaidMetadata) => void
  onExit?: (error: PlaidError | null, metadata: PlaidMetadata) => void
  onEvent?: (eventName: string, metadata: PlaidMetadata) => void
}

export interface PlaidHandler {
  open: () => void
  destroy: () => void
}

export interface PlaidMetadata {
  institution?: {
    name: string
    institution_id: string
  }
  account?: {
    id: string
    name: string
  }
  [key: string]: unknown
}

export interface PlaidError {
  error_type: string
  error_code: string
  error_message: string
  display_message?: string
}

export interface NinjaConfig {
  access_token: string
  container_id: string
  consumer_data: {
    consumer_id: string
    first_name: string
    last_name: string
    reference_date: string
  }
  onSuccess: (data: NinjaSuccessData) => void
  onError: (data: NinjaErrorData) => void
  onClose: (data: NinjaCloseData) => void
}

export interface NinjaSuccessData {
  product_request_id?: string
  request_id?: string
  [key: string]: unknown
}

export interface NinjaErrorData {
  message?: string
  status?: string
  [key: string]: unknown
}

export interface NinjaCloseData {
  [key: string]: unknown
}


